// Implements: F-UIX-023, F-MCK-018, F-MCK-019, F-UIX-009, F-UIX-010, F-UIX-013
/**
 * Where a mockup lives, and the way to move it.
 *
 * The selector follows the SERVER: the value shown is what the person picked
 * until a change settles, and then it goes back to what `GET /mockups/{id}`
 * says, whether the change worked (the server moved it) or was refused (it did
 * not). The options come from the seats of the session, so nobody is offered a
 * scope they hold no seat in, plus the viewer's own personal scope when the viewer owns the
 * mockup (owner decision 2026-10-26); the server still decides (`403`/`422`), and its
 * refusal is shown as a notice.
 *
 * Owner report, round 3: it says what moving changes (where the mockup is kept, who sees it, which
 * budget its consumption is charged to), and when the session holds no other seat to move it to,
 * it says so in words, visibly and as the field's description, instead of a silent disabled field.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useSession } from "../../../shared/api/session";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { ScopeSelector } from "../components/ScopeSelector";
import { buildScopeOptions, creatableScopeOptions, scopeValue } from "../components/scopeOptions";
import type { ScopeOption } from "../components/scopeOptions";
import { useMockupActions } from "../hooks/useMockupActions";
import { useMockupDetail } from "../hooks/useMockupDetail";
import styles from "./ScopeChangeContainer.module.css";

export interface ScopeChangeContainerProps {
  readonly mockupId: string;
}

export function ScopeChangeContainer({ mockupId }: ScopeChangeContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const { state } = useSession();
  const mockup = useMockupDetail(mockupId);
  const { changeScope } = useMockupActions();
  const { push } = useToast();
  const [chosen, setChosen] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (mockup.isPending || state.status === "loading") {
    return <LoadingState label={t("common.state.loading")} />;
  }
  if (mockup.isError) {
    return (
      <ErrorState
        error={mockup.error}
        onRetry={() => {
          void mockup.refetch();
        }}
      />
    );
  }
  if (state.status !== "authenticated") return null;

  const current = scopeValue(mockup.data.scope_kind, mockup.data.scope_id, state.view.user.id);
  // The viewer's own personal scope is offered only for a mockup the viewer owns: the server
  // moves a mockup to its OWNER's personal scope, so for somebody else's it is not "Personal".
  const options =
    mockup.data.owner_id === state.view.user.id
      ? creatableScopeOptions(state.view.scopes, t("scope.personal"))
      : buildScopeOptions(state.view.scopes);
  // A scope the session holds no seat for (revoked meanwhile, say) is still the
  // truth of the mockup, so it is shown rather than hidden. So is a personal scope the list
  // lacks: the viewer's own reads "Personal", somebody else's says so.
  const shown = options.some((option) => option.value === current)
    ? options
    : [
        ...options,
        {
          value: current,
          label:
            mockup.data.scope_kind !== "personal"
              ? mockup.data.scope_id
              : current === "personal:"
                ? t("scope.personal")
                : t("scope.personalOther"),
          kind: mockup.data.scope_kind as ScopeOption["kind"],
          id: mockup.data.scope_id,
        },
      ];
  const value = chosen ?? current;
  const target = shown.find((option) => option.value === value);
  const nowhereElse = shown.every((option) => option.value === current);
  const currentLabel = shown.find((option) => option.value === current)?.label ?? "";
  const onlyHint = nowhereElse ? t("scope.move.only", { name: currentLabel }) : undefined;

  const change = async (): Promise<void> => {
    if (target === undefined) return;
    setSaving(true);
    const refusal = await changeScope(mockupId, target.kind, target.id);
    setSaving(false);
    setChosen(null);
    push(
      refusal === null
        ? { tone: "success", message: t("scope.changed") }
        : { tone: "danger", message: userMessage(refusal, t("scope.failed")) },
    );
  };

  return (
    <div className={styles.scope}>
      <p className={styles.explain}>{t("scope.move.explain")}</p>
      <ScopeSelector options={shown} value={value} onChange={setChosen} disabled={saving || nowhereElse} hint={onlyHint} />
      <Button
        variant="secondary"
        size="sm"
        loading={saving}
        disabled={value === current}
        onClick={() => {
          void change();
        }}
      >
        {t("scope.change")}
      </Button>
    </div>
  );
}
